<script setup lang="ts">
import { ref, onMounted, nextTick } from "vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { required } from "../../../../utils/validation";
import {
  CashierDuty,
  Item,
  PosStation,
  RecordStatus,
  Warehouse,
} from "../../../../types/models";
import {
  documentActions,
  downloadPDFBlob,
  emailPDF,
  formatDate,
  isEmpty,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import { tokenManager } from "../../../../api/utils/tokenManager";
import ItemStockGrid from "../../../../components/ui/Templates/ItemStockGrid.vue";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import GridInput from "../../../../components/ui/form/GridInput.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus" | string;
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  recordStatus?: RecordStatus[];
  cartItems?: Item[];
  posStation?: PosStation | undefined
  cashierDuty?: CashierDuty | null
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save"): void;
  (e: "viewDocument", data: any, title: string, routePath: string): void;
}>();

const isReadOnly = ref<boolean>(props.mode === "view");

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  setFormData,
  resetForm,
} = useFormValidation({
  id: props.data?.id || "",
  projects_id: props.data?.projects_id || "",
  outlets_id: props.data?.outlets_id || "",
  currency: props.data?.currency || tokenManager.getSetting("default_currency", true) || "USD",
  exchange_rate: props.data?.exchange_rate || 1,
  currencys_id: props.data?.currencys_id || 1,
  total: props.data?.total || 0,
  warehouses_id: props.data?.warehouses_id || "",
  date_time: props.data?.date_time || new Date().toISOString().split("T")[0],
  prefix: props.data?.prefix || "",
  suffix: props.data?.suffix || "",
  ref_no: props.data?.ref_no || "",
  dodument_no: props.data?.dodument_no || "",
  attachment: props.data?.attachment || "",
  status: props.data?.status || "Pending",
  items: props.data?.items || [],
  document_no: props.data?.document_no || "",
  uid: props.data?.uid || "",
  download: false,
  action: props.data?.action || "",
  cashier_dutys_id: props.data?.cashier_dutys_id || props.cashierDuty?.id,
  pos_stations_id: props.data?.pos_stations_id || props.posStation?.id,
});

const warehouse = ref<Warehouse>();

//setFieldRules("outlets_id", [required("Select Outlet")]);
setFieldRules("projects_id", [required("Select Project")]);
setFieldRules("warehouses_id", [required("Select Warehouse")]);
//setFieldRules("transit_warehouses_id", [required("Select Transit Warhouse")]);
setFieldRules("items", [required("Select at least 1 item")]);

const handleOutletSelected = async (item: any) => {
  console.log(item);
  fields.value.warehouses_id.value = item.warehouses_id;
  prepareItemRow();
};

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setFormData(response.data);
    fields.value.date_time.value = formatDate(response.data.date_time);
  }
};

const validateGrid = async (): Promise<boolean> => {
  for (const item of formData.value.items) {
    if (item.tracked_by === "Batch") {
      const batch_qty = item.batches?.reduce(
        (sum: number, batch: { quantity: number }) => {
          if (typeof batch.quantity === "number") {
            return sum + batch.quantity;
          }
          return sum;
        },
        0
      );
      if (batch_qty !== item.inventory_qty) {
        return false;
      }
    } else if (item.tracked_by === "Serial") {
      const serial_qty = item.serials?.reduce(
        (sum: number, serial: { quantity: number }) => {
          if (typeof serial.quantity === "number") {
            return sum + serial.quantity;
          }
          return sum;
        },
        0
      );
      if (serial_qty !== item.inventory_qty) {
        return false;
      }
    }
  }
  return true;
};

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (validateForm()) {
    const isValid = await validateGrid();
    if (!isValid) {
      MessageBox.show(
        "There are some Batch/Serial Quantity differences",
        "warning"
      );
      return;
    }
    let items = formData.value.items.filter((e: any) => e.items_id != "");
    if (items.length <= 0) {
      fields.value.items.error = "Select at least 1 item";
      //MessageBox.show("Select at least 1 item", "warning");
      return;
    }
    formData.value.cashier_dutys_id = props.cashierDuty?.id
    formData.value.outlets_id = props.posStation?.outlets_id
    formData.value.pos_stations_id = props.posStation?.id
    formData.value.download = download || share || print;
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show("saved successfully");
      emit("save");
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDFBlob(response.data.file, 'Stock Request #: ' + response.data.data.document_no);
      } else if (mail) {
        emailPDF(props.endPoint, response.data.uid);
      } else if (share) {
        sharePDFBlob(
          response.data.file, 'Stock Request #: ' + response.data.data.document_no
        );
      } else if (print) {
        printPDFBlob(response.data.file);
      }
      setFormData(response.data.data);
    }
  }
};

const clearForm = () => {
  resetForm();

  fields.value.date_time.value = formatDate(new Date());
  fields.value.warehouses_id.value = tokenManager.getSetting(
    "default_warehouse",
    true
  );
  isReadOnly.value = false;

  nextTick(() => {
    const input = document.getElementById("customers_id");
    if (input) {
      input.focus();
      (input as HTMLInputElement).select();
    }
  });
};

const allowNewItemRow = ref(false);

const prepareItemRow = () => {
  if (
    !isEmpty(formData.value.warehouses_id) &&
    !isEmpty(formData.value.projects_id)
  ) {
    if (props.cartItems) prepareCartItems();
    allowNewItemRow.value = true;
  } else {
    allowNewItemRow.value = false;
  }
};

const prepareCartItems = async () => {
  const ids = props.cartItems?.map((item) => item.id);
  const response = await api.post(
    "clients/inventory/item-management/items/items",
    {
      ids: ids,
      warehouses_id: formData.value.warehouses_id,
    }
  );
  if (response.success == true) {
    if (itemGridRef.value) {
      itemGridRef.value.handleSelectedItems(response.data);
    }
  }
};

const itemGridRef = ref<InstanceType<typeof ItemStockGrid> | null>(null);

const updateItems = (items: Item[] | []) => {
  formData.value.items = items;
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Stock Issue# " + formData.value.document_no);
};

const formMode = ref('')

const showForm = ref(false);
onMounted(() => {  
  showForm.value = true;
  formMode.value = props.mode
  isReadOnly.value = props.mode == "view";
  if (props.mode != "create") {
    getRecord();
  } else {
    clearForm();
    if(props.posStation) {
      fields.value.warehouses_id.value = props.posStation?.outlet?.warehouses_id          
      fields.value.outlets_id.value = props.posStation?.outlets_id
    }
  }
});
</script>

<template>
  <ModalFormComponent width="100%" @close="$emit('close')">
    <template #header>
      <!-- Header -->
      <DocumentHeader
        :title="title"
        :end-point="endPoint"
        :data="formData"
        :readonly="isReadOnly"
        :show-drop-down="isReadOnly"
        @close="$emit('close')"
        :permissions="permissions"
        @clear="clearForm"
        @new="clearForm"
        @save="handleSubmit(false)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      >
        <template #dropdownbuttons> </template>
      </DocumentHeader>
    </template>
    <!-- Main Content - Scrollable -->
    <template #form>
      <!--header-->
      <div class="grid md:grid-cols-5 grid-cols-2 gap-6 mb-8">
        <SearchSelect
          label="Project"
          end-point="revenue/master-data/projects"
          v-model="fields.projects_id.value"
          @select="prepareItemRow"
          :readonly="isReadOnly"
          :error="
            fields.projects_id.touched ? fields.projects_id.error : null
          "
          @blur="fields.projects_id.touched = true"
        />
        <SearchSelect
          label="Outlet"
          end-point="system/company/outlets"
          v-model="fields.outlets_id.value"
          :readonly="isReadOnly || !isEmpty(props.posStation?.outlets_id)"
          @select="handleOutletSelected"
          :error="
            fields.outlets_id.touched ? fields.outlets_id.error : null
          "
          @blur="fields.outlets_id.touched = true"
        />
        <SearchSelect
          label="Warehouse"
          end-point="system/company/warehouses"
          v-model="fields.warehouses_id.value"
          :readonly="isReadOnly || !isEmpty(formData.warehouses_id)"
          @select="prepareItemRow"
          :error="
            fields.warehouses_id.touched ? fields.warehouses_id.error : null
          "
          @blur="fields.warehouses_id.touched = true"
        />
        <div class="col-span-2 grid grid-cols-3 gap-6">
          <DateInput
            v-model="fields.date_time.value"
            label="Date"
            :readonly="isReadOnly"
            :error="fields.date_time.touched ? fields.date_time.error : null"
            @blur="fields.date_time.touched = true" />
          <TextInput
            v-model="fields.ref_no.value"
            v-if="mode != 'create'"
            label="Reference No"
            :readonly="true"
            :error="fields.ref_no.touched ? fields.ref_no.error : null"
            @blur="fields.ref_no.touched = true" />

        </div>
      </div>
      <GridInput label="Items" :error="fields.items.touched ? fields.items.error : null">
        <ItemStockGrid
        ref="itemGridRef"
        :items="formData.items"
        :readonly="isReadOnly"
        :currency="formData.currency"
        :exchange_rate="formData.exchange_rate"
        :warehouses_id="formData.warehouses_id"
        :allow-change-price="false"
        :allow-discount="true"
        :allow-new-row="allowNewItemRow"
        :shelf-managed="warehouse?.shelf_managed ?? false"
        :shelfs="warehouse?.shelfs"
        @items="updateItems"
      />
      </GridInput>
    </template>
    <!-- Footer -->
    <template #footer>
      <DocumentFooter
        :data="formData"
        :readonly="isReadOnly"
        :end-point="endPoint"
        :permissions="permissions"
        :title="title"
        convert-module="Debit Note"
        @close="$emit('close')"
        @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @new="clearForm"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        :show-share-icons="formData.items.length > 0"
      />
    </template>
  </ModalFormComponent>
</template>
